import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './DevisForm.css';
import apiService, { Client, Product, DevisProduct, DevisInfo, Server } from '../services/api';

interface DevisFormProps {
  server: Server;
}

interface ProductRow {
  id: number;
  produitName: string;
  quantity: number | string;
  prix_ht: number;
  remise: number;
  total: number;
  searchTerm: string;
  showDropdown: boolean;
}

const DevisForm: React.FC<DevisFormProps> = ({ server }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientSearchTerm, setClientSearchTerm] = useState<string>('');
  const [productRows, setProductRows] = useState<ProductRow[]>([]);
  const [note, setNote] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [totalHT, setTotalHT] = useState(0);
  const [totalTTC, setTotalTTC] = useState(0);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  // Fetch clients on component mount
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsData = await apiService.getClients();
        setClients(clientsData);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, []);

  // Fetch products when client changes
  useEffect(() => {
    if (selectedClientId) {
      const fetchProducts = async () => {
        try {
          setIsLoading(true);
          const productsData = await apiService.getProducts(selectedClientId);
          setProducts(productsData.product);
        } catch (error) {
          console.error('Error fetching products:', error);
        } finally {
          setIsLoading(false);
        }
      };

      fetchProducts();
    }
  }, [selectedClientId]);

  // Calculate totals when product rows change
  const calculateTotals = useCallback(() => {
    const totalHT = productRows.reduce((sum, row) => sum + row.total, 0);
    const totalTTC = totalHT * 1.2; // 20% VAT
    setTotalHT(totalHT);
    setTotalTTC(totalTTC);
  }, [productRows]);

  useEffect(() => {
    calculateTotals();
  }, [calculateTotals]);

  // Filtered clients based on search
  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients.slice(0, 20);
    return clients
      .filter(client =>
        client.nom_client.toLowerCase().includes(clientSearchTerm.toLowerCase())
      )
      .slice(0, 15);
  }, [clients, clientSearchTerm]);

  // Monitor client selection state
  useEffect(() => {
    if (selectedClientId) {
      console.log('✅ Client selected:', {
        id: selectedClientId,
        name: clients.find(c => c.id_client === selectedClientId)?.nom_client,
        searchTerm: clientSearchTerm,
      });
    }
  }, [selectedClientId, clientSearchTerm, clients]);

  // Filtered products based on search
  const getFilteredProducts = useCallback(
    (searchTerm: string) => {
      if (!searchTerm) return products.slice(0, 15);
      return products
        .filter(
          product =>
            product.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.description.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .slice(0, 12);
    },
    [products]
  );

  // Add product row
  const addProductRow = useCallback(() => {
    const newRow: ProductRow = {
      id: Date.now(),
      produitName: '',
      quantity: '',
      prix_ht: 0,
      remise: 0,
      total: 0,
      searchTerm: '',
      showDropdown: false,
    };
    setProductRows(prev => [...prev, newRow]);
  }, []);

  // Remove product row
  const removeProductRow = useCallback((id: number) => {
    setProductRows(prev => prev.filter(row => row.id !== id));
  }, []);

  // Update product row
  const updateProductRow = useCallback((id: number, field: keyof ProductRow, value: any) => {
    setProductRows(prevRows =>
      prevRows.map(row => {
        if (row.id === id) {
          const updatedRow = { ...row, [field]: value };
          
          if (field === 'quantity' || field === 'prix_ht' || field === 'remise') {
            const prixHT = parseFloat(updatedRow.prix_ht.toString()) || 0;
            const quantity = parseFloat(updatedRow.quantity.toString()) || 0;
            const remise = parseFloat(updatedRow.remise.toString()) || 0;
            updatedRow.total = prixHT * quantity * (1 - remise / 100);
          }
          return updatedRow;
        }
        return row;
      })
    );
  }, []);

  // Update product search term for specific row
  const updateProductSearchTerm = useCallback((rowId: number, searchTerm: string) => {
    setProductRows(prevRows =>
      prevRows.map(row =>
        row.id === rowId ? { ...row, searchTerm, showDropdown: true } : row
      )
    );
  }, []);

  // Toggle product dropdown for specific row
  const toggleProductDropdown = useCallback((rowId: number, show: boolean) => {
    setProductRows(prevRows =>
      prevRows.map(row => (row.id === rowId ? { ...row, showDropdown: show } : row))
    );
  }, []);

  // Handle client selection
  const handleClientSelect = useCallback(
    (client: Client) => {
      setShowClientDropdown(false);
      setSelectedClientId(client.id_client);
      setClientSearchTerm(client.nom_client);
    },
    []
  );

  // Handle client search input changes
  const handleClientSearchChange = useCallback((value: string) => {
    setClientSearchTerm(value);
    setShowClientDropdown(true);
    
    if (!value) {
      setSelectedClientId('');
    }
  }, []);

  // Handle client search focus
  const handleClientSearchFocus = useCallback(() => {
    setShowClientDropdown(true);
  }, []);

  // Handle client dropdown click outside
  const handleClientDropdownClickOutside = useCallback(() => {
    setShowClientDropdown(false);
  }, []);

  // Clear client selection
  const handleClearClient = useCallback(() => {
    setSelectedClientId('');
    setClientSearchTerm('');
    setShowClientDropdown(false);
  }, []);

  // Handle submit
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate required fields
      if (!selectedClientId || productRows.length === 0) {
        alert('Veuillez sélectionner un client et ajouter au moins un produit');
        return;
      }

      // Validate quantities
      const invalidQuantities = productRows.filter(row => 
        !row.quantity || parseFloat(row.quantity.toString()) <= 0
      );
      if (invalidQuantities.length > 0) {
        alert('Veuillez saisir une quantité valide (supérieure à 0) pour tous les produits');
        return;
      }

      setIsSubmitting(true);

      try {
        const devisProducts: DevisProduct[] = productRows.map(row => {
          const quantity = typeof row.quantity === 'string' ? parseFloat(row.quantity) : row.quantity;
          return {
            id_prix: row.produitName,
            id_produit_stock: row.produitName,
            description: products.find(p => p.id_prix === row.produitName)?.description || '',
            remise: row.remise,
            prix_ht: row.prix_ht,
            quantity: quantity || 0,
          };
        });

        const selectedClient = clients.find(c => c.id_client === selectedClientId);
        if (!selectedClient) {
          throw new Error('Client not found');
        }

        const devisInfo: DevisInfo = {
          id_serveur: server.id_serveur,
          id_client: selectedClientId,
          ice_client: selectedClient.ice_client || '',
          note: note,
        };

        const devisData = {
          infos: [devisInfo],
          products: devisProducts,
        };

        const response = await apiService.createDevis(devisData);
        
        // Show success popup
        setShowSuccessPopup(true);
        
        // Clear all form inputs
        setSelectedClientId('');
        setClientSearchTerm('');
        setProductRows([]);
        setNote('');
        setTotalHT(0);
        setTotalTTC(0);
        
        // Open PDF in new tab
        window.open(response.url, '_blank');
        
        // Popup will stay visible until user manually closes it

        if (window.matchMedia('(max-width: 1024px)').matches) {
          const downloadLink = document.createElement('a');
          downloadLink.href = response.url;
          downloadLink.download = 'devis-lacaisse.pdf';
          downloadLink.style.display = 'none';
          document.body.appendChild(downloadLink);
          downloadLink.click();
          document.body.removeChild(downloadLink);
        }
      } catch (error) {
        console.error('Error creating devis:', error);
        alert('Erreur lors de la création du devis');
      } finally {
        setIsSubmitting(false);
      }
    },
    [selectedClientId, productRows, products, server.id_serveur, note, clients]
  );

  // Handle product change
  const handleProductChange = useCallback(
    (rowId: number, productId: string) => {
      const product = products.find(p => p.id_prix === productId);
      if (product) {
        // Reset quantity and remise when changing product
        updateProductRow(rowId, 'produitName', productId);
        updateProductRow(rowId, 'prix_ht', product.prix_ht);
        updateProductRow(rowId, 'searchTerm', product.designation); // Display product name
        updateProductRow(rowId, 'quantity', ''); // Reset quantity to empty
        updateProductRow(rowId, 'remise', 0); // Reset remise to 0
        toggleProductDropdown(rowId, false);
      }
    },
    [products, updateProductRow, toggleProductDropdown]
  );

  // Handle product selection
  const handleProductSelect = useCallback(
    (product: Product, rowId: number) => {
      console.log('Product selected:', product.designation);
      handleProductChange(rowId, product.id_prix);
    },
    [handleProductChange]
  );

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const input = document.getElementById('client-search');
      const dropdown = document.querySelector('.client-dropdown');
      
      // Check if click is inside either the input or the dropdown
      const isInsideClientArea = (input && input.contains(target)) || (dropdown && dropdown.contains(target));
      
      if (!isInsideClientArea) {
        handleClientDropdownClickOutside();
      }

      const isOutsideProductSearch = !Array.from(
        document.querySelectorAll('.product-search-container')
      ).some(container => container.contains(target));

      if (isOutsideProductSearch) {
        setProductRows(prevRows =>
          prevRows.map(row => ({ ...row, showDropdown: false }))
        );
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="devis-form-container">
      <div className="devis-form-card card fade-in">
        <div className="card-header">
          <div className="logo-container" style={{ 
            textAlign: 'center', 
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
            padding: '15px',
            borderRadius: '20px',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Subtle background pattern */}
            <div style={{
              position: 'absolute',
              top: '-50%',
              left: '-50%',
              width: '200%',
              height: '200%',
              background: 'radial-gradient(circle, rgba(202, 10, 10, 0.03) 0%, transparent 70%)',
              animation: 'logoFloat 6s ease-in-out infinite'
            }} />
            
            <img 
              src="https://macaisse.ma/_next/static/media/logo.3d661f48.png" 
              alt="Lacaisse Logo" 
              style={{ 
                maxWidth: '180px', 
                height: 'auto',
                filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.12))',
                position: 'relative',
                zIndex: 2,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.filter = 'drop-shadow(0 12px 24px rgba(0,0,0,0.15))';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.filter = 'drop-shadow(0 8px 16px rgba(0,0,0,0.12))';
              }}
            />
            
            {/* Elegant accent line */}
            <div style={{
              width: '60px',
              height: '3px',
              background: 'linear-gradient(90deg, #ca0a0a, #ff6b6b)',
              margin: '2px auto 0',
              borderRadius: '2px',
              boxShadow: '0 2px 8px rgba(202, 10, 10, 0.3)'
            }} />
          </div>
          <h1 className="card-title">Générer un bon de commande</h1>
          <p className="card-subtitle">
            Bienvenue {server.nom_serveur}, merci de compléter le formulaire ci-dessous pour générer un bon de commande.
          </p>
        </div>

        <div className="card-body">

          
          <form onSubmit={handleSubmit} className="devis-form">
            {/* Client Selection with Search */}
            <div className="form-group">
              <label htmlFor="client-search">Client</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="client-search"
                  type="text"
                  placeholder="Tapez pour rechercher un client..."
                  value={clientSearchTerm}
                  onChange={(e) => handleClientSearchChange(e.target.value)}
                  onFocus={() => handleClientSearchFocus()}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    border: '2px solid #ddd',
                    borderRadius: '16px',
                    fontSize: '16px',
                    outline: 'none',
                  }}
                />
                {selectedClientId && (
                  <div
                    style={{
                      background: '#e8f5e8',
                      border: '1px solid #28a745',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginTop: '8px',
                      color: '#28a745',
                      fontWeight: '600',
                    }}
                  >
                    ✓ Client sélectionné: {clients.find(c => c.id_client === selectedClientId)?.nom_client || ''}
                                        <button
                      type="button"
                      onClick={() => handleClearClient()}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#dc3545',
                        cursor: 'pointer',
                        float: 'right',
                        fontSize: '16px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                {showClientDropdown && (
                  <div
                    className="client-dropdown"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '2px solid #ca0a0a',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
                      zIndex: 9999,
                      maxHeight: '300px',
                      overflowY: 'auto',
                      marginTop: '4px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {filteredClients.length > 0 ? (
                      filteredClients.map((client, index) => (
                        <div
                          key={client.id_client}
                          style={{
                            padding: '16px 20px',
                            cursor: 'pointer',
                            borderBottom: '1px solid #eee',
                            background: index % 2 === 0 ? '#f8f9fa' : 'white',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e3f2fd';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = index % 2 === 0 ? '#f8f9fa' : 'white';
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleClientSelect(client);
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', color: '#333' }}>{client.nom_client}</span>
                            <span
                              style={{
                                fontSize: '12px',
                                background: '#f0f0f0',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                color: '#666',
                              }}
                            >
                              ID: {client.id_client}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: '20px',
                          textAlign: 'center',
                          color: '#666',
                          fontSize: '14px',
                        }}
                      >
                        Aucun client trouvé
                      </div>
                    )}
                    {filteredClients.length > 0 && (
                      <div
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          background: '#f8f9fa',
                          color: '#666',
                          fontSize: '12px',
                          borderTop: '1px solid #eee',
                        }}
                      >
                        {clients.length > filteredClients.length &&
                          `+${clients.length - filteredClients.length} autres clients`}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Products Table */}
            <div className="products-section">
              <div className="products-header">
                <h3>Produits</h3>
                <button type="button" onClick={addProductRow} className="btn btn-success">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Ajouter un produit
                </button>
              </div>

              {/* Desktop Table View */}
              <div className="table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Quantité</th>
                      <th>Prix HT</th>
                      <th>Remise (%)</th>
                      <th>Montant</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <div className="search-container product-search-container">
                            <input
                              type="text"
                              value={row.searchTerm}
                              onChange={(e) => updateProductSearchTerm(row.id, e.target.value)}
                              onFocus={() => toggleProductDropdown(row.id, true)}
                              placeholder="Rechercher un produit..."
                              className="form-control search-input"
                              data-row-id={row.id}
                              required
                            />
                            {row.showDropdown && getFilteredProducts(row.searchTerm).length > 0 && (
                              <div className="search-dropdown product-dropdown" data-dropdown-row={row.id}>
                                {getFilteredProducts(row.searchTerm).map(product => (
                                  <button
                                    key={product.id_prix}
                                    type="button"
                                    className="search-item"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleProductSelect(product, row.id);
                                    }}
                                  >
                                    <span className="item-name">{product.designation}</span>
                                    <span className="item-price">{product.prix_ht} MAD</span>
                                  </button>
                                ))}
                                {products.length > getFilteredProducts(row.searchTerm).length && (
                                  <div className="search-more">
                                    +{products.length - getFilteredProducts(row.searchTerm).length} autres produits
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.quantity}
                            onChange={(e) => {
                              const value = e.target.value;
                              updateProductRow(row.id, 'quantity', value);
                            }}
                            className="form-control"
                            min="0.01"
                            step="0.01"
                            required
                            placeholder="Quantité"
                            style={{
                              WebkitAppearance: 'none',
                              MozAppearance: 'textfield',
                              appearance: 'textfield'
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.prix_ht}
                            onChange={(e) => updateProductRow(row.id, 'prix_ht', parseFloat(e.target.value))}
                            className={`form-control ${row.produitName ? 'prix-ht-disabled' : ''}`}
                            step="0.01"
                            min="0"
                            required
                            disabled={!!row.produitName} // Disable when product is selected
                            title={row.produitName ? 'Prix automatiquement défini par le produit sélectionné' : ''}
                          />
                        </td>
                        <td>
                          <select
                            value={row.remise}
                            onChange={(e) => updateProductRow(row.id, 'remise', parseFloat(e.target.value))}
                            className="form-control"
                          >
                            {[0, 5, 10, 15, 20, 25, 30, 35, 40, 50].map(remise => (
                              <option key={remise} value={remise}>{remise}%</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.total.toFixed(2)}
                            className="form-control montant-field"
                            disabled
                            readOnly
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => removeProductRow(row.id)}
                            className="btn btn-danger btn-sm"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="products-table-mobile">
                {productRows.map((row) => (
                  <div key={row.id} className="product-card">
                    <div className="product-card-header">
                      <h4 className="product-card-title">Produit #{row.id}</h4>
                      <button
                        type="button"
                        onClick={() => removeProductRow(row.id)}
                        className="product-card-remove"
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="product-card-fields">
                      <div className="product-field-group">
                        <label className="product-field-label">Produit</label>
                        <div className="search-container product-search-container" style={{ position: 'relative' }}>
                          <input
                            type="text"
                            value={row.searchTerm}
                            onChange={(e) => updateProductSearchTerm(row.id, e.target.value)}
                            onFocus={() => toggleProductDropdown(row.id, true)}
                            placeholder="Rechercher un produit..."
                            className="product-field-input"
                            data-row-id={row.id}
                            required
                          />
                          {row.showDropdown && getFilteredProducts(row.searchTerm).length > 0 && (
                            <div className="search-dropdown product-dropdown" 
                              data-dropdown-row={row.id}
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                background: 'white',
                                border: '2px solid #ca0a0a',
                                borderRadius: '12px',
                                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                                zIndex: 1000,
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginTop: '4px'
                              }}
                            >
                              {getFilteredProducts(row.searchTerm).map(product => (
                                <button
                                  key={product.id_prix}
                                  type="button"
                                  className="search-item"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleProductSelect(product, row.id);
                                  }}
                                  style={{
                                    width: '100%',
                                    padding: '12px 16px',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f0f0f0',
                                    transition: 'background 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#f8f9fa';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'none';
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ 
                                      fontSize: '14px', 
                                      color: '#333',
                                      fontWeight: '500',
                                      flex: 1,
                                      marginRight: '10px'
                                    }}>
                                      {product.designation}
                                    </span>
                                    <span style={{
                                      fontSize: '12px',
                                      color: '#ca0a0a',
                                      fontWeight: '600',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {product.prix_ht} MAD
                                    </span>
                                  </div>
                                </button>
                              ))}
                              {products.length > getFilteredProducts(row.searchTerm).length && (
                                <div style={{
                                  padding: '8px 16px',
                                  textAlign: 'center',
                                  background: '#f8f9fa',
                                  color: '#666',
                                  fontSize: '11px',
                                  borderTop: '1px solid #eee'
                                }}>
                                  +{products.length - getFilteredProducts(row.searchTerm).length} autres produits
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="product-field-group">
                        <label className="product-field-label">Quantité</label>
                        <input
                          type="number"
                          value={row.quantity}
                          onChange={(e) => {
                            const value = e.target.value;
                            updateProductRow(row.id, 'quantity', value);
                          }}
                          className="product-field-input"
                          min="0.01"
                          step="0.01"
                          required
                          placeholder="Quantité"
                          style={{
                            WebkitAppearance: 'none',
                            MozAppearance: 'textfield',
                            appearance: 'textfield'
                          }}
                        />
                      </div>
                      
                      <div className="product-field-group">
                        <label className="product-field-label">Prix HT</label>
                        <input
                          type="number"
                          value={row.prix_ht}
                          onChange={(e) => updateProductRow(row.id, 'prix_ht', parseFloat(e.target.value))}
                          className={`product-field-input ${row.produitName ? 'prix-ht-disabled' : ''}`}
                          step="0.01"
                          min="0"
                          required
                          disabled={!!row.produitName}
                          title={row.produitName ? 'Prix automatiquement défini par le produit sélectionné' : ''}
                        />
                      </div>
                      
                      <div className="product-field-group">
                        <label className="product-field-label">Remise (%)</label>
                        <select
                          value={row.remise}
                          onChange={(e) => updateProductRow(row.id, 'remise', parseFloat(e.target.value))}
                          className="product-field-input"
                        >
                          {[0, 5, 10, 15, 20, 25, 30, 35, 40, 50].map(remise => (
                            <option key={remise} value={remise}>{remise}%</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    <div className="product-card-total">
                      <div className="product-card-total-label">Montant Total</div>
                      <div className="product-card-total-value">{row.total.toFixed(2)} MAD</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="totals-section">
              <div className="total-row">
                <span className="total-label">Total HT:</span>
                <span className="total-value">{totalHT.toFixed(2)} MAD</span>
              </div>
              <div className="total-row">
                <span className="total-label">Total TTC (20% TVA):</span>
                <span className="total-value">{totalTTC.toFixed(2)} MAD</span>
              </div>
            </div>

            {/* Note */}
            <div className="form-group">
              <label htmlFor="note">Note</label>
              <textarea
                id="note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="form-control"
                rows={4}
                placeholder="Ajouter une note..."
              />
            </div>

            {/* Submit Button */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={isSubmitting || productRows.length === 0}
              >
                {isSubmitting ? (
                  <>
                    <div className="spinner"></div>
                    Génération en cours...
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                    </svg>
                    Générer le bon de commande
                  </>
                )}
              </button>
              
            </div>
          </form>
        </div>
      </div>
      
      {/* Success Popup */}
      {showSuccessPopup && (
        <div className="success-popup-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div className="success-popup" style={{
            background: 'white',
            borderRadius: '20px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            maxWidth: '400px',
            width: '90%',
            animation: 'slideUp 0.4s ease-out',
            border: '2px solid #10b981',
            position: 'relative',
            zIndex: 100000
          }}>
            {/* Close button */}
            <button
              onClick={() => setShowSuccessPopup(false)}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                color: '#9ca3af',
                cursor: 'pointer',
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.color = '#6b7280';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'none';
                e.currentTarget.style.color = '#9ca3af';
              }}
            >
              ✕
            </button>
            <div className="success-icon" style={{
              width: '80px',
              height: '80px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              borderRadius: '50%',
              margin: '0 auto 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(16, 185, 129, 0.3)'
            }}>
              <svg 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="3"
                style={{ width: '40px', height: '40px' }}
              >
                <polyline points="20,6 9,17 4,12" />
              </svg>
            </div>
            
            <h3 style={{
              color: '#10b981',
              fontSize: '24px',
              fontWeight: '700',
              margin: '0 0 15px 0'
            }}>
              Bon de commande généré avec succès !
            </h3>
            
            <p style={{
              color: '#6b7280',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: '0 0 25px 0'
            }}>
              Votre bon de commande a été créé et le PDF s'ouvre dans un nouvel onglet.
            </p>
            
            <button
              onClick={() => setShowSuccessPopup(false)}
              style={{
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
              }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevisForm;